import { ChangeEvent, useCallback, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { INVALID_TYPE_FILE } from 'CorePackage/MainModule/constants/notices';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { approvedSelectOptions } from 'KnmErknmPackage/MainModule/constants/approvedSelectOptions';
import { IconFile } from 'UIPackage/IconsModule/components/IconFile';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { IApprovedSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IApprovedSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { addNoticeKnmFileSizeErrorDetailed } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';

import styles from './ErknmAttachWebDocumentModal.module.scss';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { knmHandleUploadErknmAttachWebDocument, knmRemoveVisibleModal } from '../actions/knmActions';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getMaxFileSize } from '../../../CorePackage/MainModule/common/getMaxFileSize';
import { getKnmSystemType } from '../selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { MB } from '../../../CorePackage/MainModule/constants/fileSize';

interface IErknmAttachWebDocumentModal {
  visible: boolean;
  settings: IAdminSetting[];
  knmSystemType: KnmSystemTypesEnum | undefined;
}

const ErknmAttachWebDocumentModalWithoutStore = ({
  visible,
  settings,
  knmSystemType,
}: IErknmAttachWebDocumentModal) => {
  const [document, setDocument] = useState<File | null>(null);
  const [signs, setSigns] = useState<Array<File>>([]);
  const [approvedValue, setApprovedValue] = useState<IApprovedSelectOption>();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));
  }, [dispatch, maxFileSize]);

  const isDisabledButton = !document || !approvedValue;

  const renderAttachment = (attachment: File, index?: number) => (
    <div className={styles.AttachedDocument} key={index || attachment.name}>
      <IconFile fileName={attachment.name} />
      <span className={styles.AttachedDocumentName}>{attachment.name}</span>
      <button
        className={styles.RemoveButton}
        type="button"
        onClick={index !== undefined ? handleRemoveSign(index) : handleRemoveDocument}
      >
        x
      </button>
    </div>
  );

  function handleUploadButton() {
    dispatch(knmHandleUploadErknmAttachWebDocument(document, approvedValue, signs));
  }

  const handleInputAttachment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];
        const fileName = file.name.toLocaleLowerCase();
        const acceptedTypes = e.target.accept.split(',');

        if (acceptedTypes.find(item => fileName.endsWith(item))) {
          const isSizeValid = file.size <= maxFileSize * MB;
          const isSign = fileName.split('.').pop() === 'sig';

          if (!isSizeValid) {
            handleFileSizeError();
          } else {
            isSign ? setSigns(prev => [...prev, file]) : setDocument(file);
          }
        } else {
          dispatch(
            addNoticeAction({
              renderContent: INVALID_TYPE_FILE,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT_TYPE,
            }),
          );
        }
      }
      e.target.value = '';
    },
    [dispatch],
  );

  const handleRemoveDocument = () => {
    setDocument(null);
  };

  const handleRemoveSign = (index: number) => () => {
    setSigns(prev => prev.filter((el: File, idx: number) => idx !== index));
  };

  const handleChangeApprovedValue = useCallback((value: IApprovedSelectOption) => setApprovedValue(value), []);

  const onClose = useCallback(() => {
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_ATTACH_WEB_DOCUMENT_MODAL));
    setDocument(null);
    setSigns([]);
    setApprovedValue(undefined);
  }, [dispatch]);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose}>
      <ModalHeader title="Загрузить решение по заявлению" />
      <ModalBody size="content" border="bottom">
        Для принятия решения по заявлению в случае проблем интеграции с АИК “Надзор-Web” необходимо прикрепить файл
        решения за подписью вышестоящего прокурора и указать его решение
        <div className={styles.ApprovedFieldBlock}>
          <div className="shared-label-block">
            <label className="shared-field-label" htmlFor="approved">
              Решение по заявлению
            </label>
          </div>
          <SelectInput
            options={approvedSelectOptions}
            id="approved"
            name="approved"
            size="medium"
            value={approvedValue}
            onChange={handleChangeApprovedValue}
            isClearable={false}
          />
        </div>
        <div className={styles.Attachments}>
          <div className={styles.AttachmentsContainer}>
            <div className={styles.AttachmentHeader}>
              <span>Документ</span>
              <label className={styles.AddAttachmentLabel} htmlFor="attachWebDocument">
                Добавить
              </label>
              <input
                type="file"
                id="attachWebDocument"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleInputAttachment}
              />
            </div>
            {document && renderAttachment(document)}
          </div>
          <div className={styles.AttachmentsContainer}>
            <div className={styles.AttachmentHeader}>
              <span>Подписи</span>
              <label className={styles.AddAttachmentLabel} htmlFor="attachWebSig">
                Добавить
              </label>
              <input
                type="file"
                id="attachWebSig"
                accept=".sig"
                style={{ display: 'none' }}
                onChange={handleInputAttachment}
              />
            </div>
            {signs.length ? signs.map((sign, idx) => renderAttachment(sign, idx)) : null}
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button
          text="Сохранить"
          id="saveButton"
          color="primary"
          onClick={handleUploadButton}
          disabled={isDisabledButton}
        />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  visible: !!state.knm?.knmVisibleModals?.find(item => item === KnmModalsEnum.ERKNM_ATTACH_WEB_DOCUMENT_MODAL),
  settings: getAdminFetchSettings(state),
  knmSystemType: getKnmSystemType(state),
});

export const ErknmAttachWebDocumentModal = connect(mapStateToProps)(ErknmAttachWebDocumentModalWithoutStore);
